import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ImsiMap } from "../../models/ImsiMap";
import {
  fetchImsiMaps as fetchImsiMapsApi,
  createImsiMap as createImsiMapApi,
  updateImsiMap as updateImsiMapApi,
  deleteImsiMap as deleteImsiMapApi,
} from "../../services/apiConfig";
import type { ListParams } from "../../services/api";

interface ImsiMapsState {
  imsiMaps: ImsiMap[];
  /** The current page only - the server no longer sends the whole table. */
  total: number;
  loading: "idle" | "pending" | "succeeded" | "failed";
  error: string | null;
}

const initialState: ImsiMapsState = {
  imsiMaps: [],
  total: 0,
  loading: "idle",
  error: null,
};

export const fetchImsiMaps = createAsyncThunk(
  "imsiMaps/fetchImsiMaps",
  async (params?: ListParams) => {
    const response = await fetchImsiMapsApi(params);
    return response;
  },
);

export const addImsiMap = createAsyncThunk(
  "imsiMaps/addImsiMap",
  async (data: Omit<ImsiMap, "id">) => {
    const response = await createImsiMapApi(data);
    return response;
  },
);

export const editImsiMap = createAsyncThunk(
  "imsiMaps/editImsiMap",
  async ({ id, data }: { id: number; data: Partial<ImsiMap> }) => {
    const response = await updateImsiMapApi(id, data);
    return response;
  },
);

export const removeImsiMap = createAsyncThunk(
  "imsiMaps/removeImsiMap",
  async (id: number) => {
    await deleteImsiMapApi(id);
    return id;
  },
);

const imsiMapsSlice = createSlice({
  name: "imsiMaps",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchImsiMaps.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchImsiMaps.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.imsiMaps = action.payload.data;
        state.total = action.payload.total;
      })
      .addCase(fetchImsiMaps.rejected, (state, action) => {
        state.loading = "failed";
        state.error = action.error.message ?? "Failed to fetch IMSI maps";
      })
      .addCase(addImsiMap.fulfilled, (state, action) => {
        state.imsiMaps.unshift(action.payload);
      })
      .addCase(editImsiMap.fulfilled, (state, action) => {
        const index = state.imsiMaps.findIndex(
          (map) => map.id === action.payload.id
        );
        if (index !== -1) {
          state.imsiMaps[index] = action.payload;
        }
      })
      .addCase(removeImsiMap.fulfilled, (state, action) => {
        state.imsiMaps = state.imsiMaps.filter(
          (map) => map.id !== action.payload,
        );
      });
  },
});

export default imsiMapsSlice.reducer;
